import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Globe, ArrowRight, Award, Users, Target, Truck, Building, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

// Define reusable animation variants for staggered effects
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    }
  }
};

const Home = () => {
  const highlights = [
    {
      icon: Truck,
      title: 'Medical Consumables Distribution',
      description: 'Direct distribution to healthcare practitioners across West Africa',
      link: '/services'
    },
    {
      icon: Users,
      title: 'Healthcare Consulting',
      description: 'Expert consulting for operational efficiency and growth',
      link: '/services'
    },
    {
      icon: Building,
      title: 'Business Development',
      description: 'Strategic business development for healthcare organizations',
      link: '/services'
    },
    {
      icon: Target,
      title: 'Trusted Excellence',
      description: 'Reliability, transparency, and exceptional service delivery',
      link: '/about'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-teal-50/30"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            {/* Content */}
            <motion.div
              className="space-y-8"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <div className="space-y-4">
                <motion.h1
                  className="text-4xl lg:text-6xl font-bold text-slate-900 leading-tight"
                  variants={itemVariants}
                >
                  Advancing Healthcare
                  <span className="text-teal-600 block">Delivery in West Africa</span>
                </motion.h1>
                
                <motion.p
                  className="text-xl text-slate-600 leading-relaxed max-w-2xl"
                  variants={itemVariants}
                >
                  Connecting healthcare practitioners with premium-quality medical consumables, expert consulting 
                  services, and strategic business development solutions across West Africa.
                </motion.p>
              </div>

              {/* CTA Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                variants={itemVariants}
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Get Started Today
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
                >
                  Learn More
                </Link>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
<motion.div
  className="relative"
  initial={{ scale: 0.95, opacity: 0 }}
  animate={{
    scale: 1,
    opacity: 1,
    y: ["-2%", "2%", "-2%"]
  }}
  transition={{
    scale: { duration: 0.5, delay: 0.4 }, // Initial scale and opacity animation
    y: { // Continuous floating animation
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0.4
    }
  }}
>
              <div className="relative z-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-distro/artifacts/yfyeez8e_serious-african-american-female-doctor-walking-with-patient-s-test-results-before-meeting-with-patient-doctor-is-hospital-hallway.jpg"
                  alt="Professional African American female doctor in hospital setting"
                  className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
                />
                
                {/* Floating Cards */}
                <motion.div
                  className="absolute -top-6 -left-6 bg-white p-4 rounded-xl shadow-lg border"
                  initial={{ opacity: 0, x: -50, rotate: -5 }}
                  animate={{
                    opacity: 1,
                    x: ["-5%", "5%", "-5%"],
                    y: ["-6%", "6%", "-6%"],
                    rotate: 0,
                  }}
                  transition={{
    scale: { duration: 0.5, delay: 0.4 }, // Initial scale and opacity animation
    x: { // Continuous floating animation
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0.4
    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Premium Quality</div>
                      <div className="text-sm text-slate-600">International Standards</div>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div
                  className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg border"
                  initial={{ opacity: 0, x: 50, rotate: 5 }}
                  animate={{
                    opacity: 1,
                    x: ["5%", "-5%", "5%"], // Opposite horizontal motion for card 2
                    y: ["6%", "-6%", "6%"], // Opposite vertical motion for card 2
                    rotate: 0,
                  }}
                  transition={{
     scale: { duration: 0.5, delay: 0.4 }, // Initial scale and opacity animation
    x: { // Continuous floating animation
      duration: 5.5,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0.4
    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Globe className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Trusted Network</div>
                      <div className="text-sm text-slate-600">Proven Partners</div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Background Decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-teal-100 to-blue-100 rounded-2xl transform rotate-6 scale-105 opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Preview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Why Healthcare Providers Choose Mediquip
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our unique combination of experience, network, and commitment makes us the trusted partner 
              for healthcare facilities across West Africa.
            </p>
          </div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ amount: 0.3 }}
          >
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  variants={itemVariants}
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{highlight.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{highlight.description}</p>
                  <Link
                    to={highlight.link}
                    className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium text-sm transition-colors"
                  >
                    Learn more <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              Explore Our Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-800 rounded-full text-sm font-medium mb-4">
              <Globe className="w-4 h-4 mr-2 text-teal-600" />
              Our Trusted Partners
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Strategic Healthcare Partnerships
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We collaborate with leading global healthcare companies to bring world-class 
              medical consumables and technologies to healthcare providers across West Africa.
            </p>
          </div>

          <motion.div
            className="grid md:grid-cols-3 gap-12 items-center"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ amount: 0.3 }}
          >
            {/* Lapu Medical Technology */}
            <motion.div className="group text-center" variants={itemVariants}>
              <div className="bg-white rounded-2xl p-8 mb-6 hover:shadow-lg transition-all duration-300 transform group-hover:-translate-y-2 border border-slate-100">
                <img
                  src="data:image/jpeg;base64,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"
                  alt="Lapu Medical Technology"
                  className="w-full h-28 object-contain mx-auto mb-4"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Lapu Medical Technology</h3>
              <p className="text-teal-600 font-semibold mb-3">Official Partner</p>
              <p className="text-slate-600 text-sm">
                Cardiovascular Medical Device Innovation
              </p>
            </motion.div>

            {/* Wizbiotech */}
            <motion.div className="group text-center" variants={itemVariants}>
              <div className="bg-white rounded-2xl p-8 mb-6 hover:shadow-lg transition-all duration-300 transform group-hover:-translate-y-2 border border-slate-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-distro/artifacts/rto81cp8_wizbiotech.jpg"
                  alt="Wizbiotech"
                  className="w-full h-28 object-contain mx-auto mb-4"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Wizbiotech</h3>
              <p className="text-blue-600 font-semibold mb-3">Official Partner</p>
              <p className="text-slate-600 text-sm">
                Manufacturers of Diabench 5-in-1 Rapid Blood Type and Infectious combo kit
              </p>
            </motion.div>

            {/* Zutron */}
            <motion.div className="group text-center" variants={itemVariants}>
              <div className="bg-white rounded-2xl p-8 mb-6 hover:shadow-lg transition-all duration-300 transform group-hover:-translate-y-2 border border-slate-100">
                <img
                  src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT6eid6Y3KJG9eiDHg4meEhAUWn6jMvJtIZAV6csVmOA3qyYP2w8r-fhrW2&s=10"
                  alt="Zutron"
                  className="w-full h-28 object-contain mx-auto mb-4"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Zutron Pharmaceuticals</h3>
              <p className="text-blue-600 font-semibold mb-3">Official Partner</p>
              <p className="text-slate-600 text-sm">
                Distribution of HemoType Sc
              </p>
            </motion.div>

            {/* Terumo */}
            <motion.div className="group text-center" variants={itemVariants}>
              <div className="bg-white rounded-2xl p-8 mb-6 hover:shadow-lg transition-all duration-300 transform group-hover:-translate-y-2 border border-slate-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-distro/artifacts/h51666f2_Terumo.jpg"
                  alt="Terumo Blood and Cell Technologies"
                  className="w-full h-28 object-contain mx-auto mb-4"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Terumo Blood and Cell Technologies</h3>
              <p className="text-green-600 font-semibold mb-3">Sub Dealer</p>
              <p className="text-slate-600 text-sm">
                Specialized blood and cell technology solutions for critical healthcare needs
              </p>
            </motion.div>
          </motion.div>

          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 inline-block">
              <p className="text-lg text-slate-700 font-medium">
                Trusted by leading healthcare companies worldwide to deliver excellence in West Africa
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 lg:p-12 border border-slate-100"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Ready to Transform Your Healthcare Supply Chain?
            </h2>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Contact us today so Mediquip can help your healthcare facility achieve its goals, 
              and we can, together, redefine medical consumables distribution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Contact Us Today
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
              >
                Learn About Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;